import { useEffect, useRef } from "react";

export default function Snowfall({ count = 140 }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0, h = 0, raf = 0, visible = true, t = 0;
    const flakes = [];
    const m = { x: -9999, y: -9999, on: false };
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      m.x = e.clientX - r.left; m.y = e.clientY - r.top;
      m.on = m.x >= 0 && m.y >= 0 && m.x <= r.width && m.y <= r.height;
    };
    const onLeave = () => { m.on = false; };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.offsetWidth; h = canvas.offsetHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const n = w < 640 ? Math.round(count * 0.5) : count;
    for (let i = 0; i < n; i++) {
      const depth = Math.random();
      flakes.push({ x: Math.random() * w, y: Math.random() * h, r: 0.8 + depth * 2.6, vy: 0.25 + depth * 0.8, dx: 0, dy: 0, sway: Math.random() * Math.PI * 2, a: 0.45 + depth * 0.5 });
    }

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const f of flakes) {
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${f.a})`;
        ctx.shadowColor = "rgba(0,125,108,0.35)";
        ctx.shadowBlur = f.r * 2;
        ctx.fill();
      }
    };
    const tick = () => {
      t += 0.01;
      for (const f of flakes) {
        if (m.on) {
          const ex = f.x - m.x, ey = f.y - m.y;
          const d2 = ex * ex + ey * ey;
          if (d2 < 19600 && d2 > 1) {
            const d = Math.sqrt(d2);
            const k = (1 - d / 140) * 0.35;
            f.dx += (ex / d) * k - (ey / d) * k * 0.8;
            f.dy += (ey / d) * k + (ex / d) * k * 0.8;
          }
        }
        f.dx *= 0.94; f.dy *= 0.94;
        f.y += f.vy + f.dy;
        f.x += Math.sin(t + f.sway) * 0.35 + 0.12 + f.dx;
        if (f.y < -6) f.y = -6;
        if (f.y > h + 4) { f.y = -4; f.x = Math.random() * w; }
        if (f.x > w + 4) f.x = -4;
        if (f.x < -6) f.x = w + 4;
      }
      draw();
      raf = visible ? requestAnimationFrame(tick) : 0;
    };

    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible && !raf && !reduce) raf = requestAnimationFrame(tick);
    });
    io.observe(canvas);
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    if (reduce) draw();
    else raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
    };
  }, [count]);

  return <canvas ref={ref} aria-hidden="true" data-testid="hero-snowfall" className="pointer-events-none absolute inset-0 h-full w-full" />;
}
